import React, { useCallback, useContext, useEffect, useState, useRef } from "react";
import { Redirect, useHistory } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Brain,
  Heart,
  Activity,
  Briefcase,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Sparkles,
  Target,
  Award
} from "lucide-react";
import { ReactComponent as Guy } from "../../assets/svg/guy.svg";
import LayoutAuth from "../../blocks/layout-auth/layout-auth";
import CustomRangeSlider from "../../components/custom-range-slider/custom-range-slider";
import API from "../../utils/api";
import useModal from "../../hooks/use-modal";
import { MainStore } from "../../stores/Main";
import { usePostHog } from "posthog-js/react";
import { trackQuestionnaireSubmitted, trackSentimentQuestionnaireSkipped } from "../../utils/posthogTracking";

const SentimentQuestionnaire = (props) => {
  const history = useHistory();
  const [questions, setQuestions] = useState({});
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const { closeModal } = useModal();
  const mainStore = useContext(MainStore);
  const posthog = usePostHog();
  const startTimeRef = useRef(Date.now());

  const getQuestions = async () => {
    try {
      const response = await API.get("/sentiment-questions");
      if (
        response.data.sentiment_question &&
        response.data.sentiment_question.length > 0
      ) {
        let newQuestionA = [undefined];
        let newQuestions = {};
        let newAnswers = {};
        // Order by order
        for (let i = 0; i < response.data.sentiment_question.length; i++) {
          newQuestionA[response.data.sentiment_question[i].order] =
            response.data.sentiment_question[i];
          newAnswers[response.data.sentiment_question[i].id] = undefined;
        }
        setAnswers(newAnswers);
        // Split by section
        for (let i = 0; i < newQuestionA.length; i++) {
          if (newQuestionA[i] === undefined) continue;
          if (!Array.isArray(newQuestions[newQuestionA[i].section]))
            newQuestions[newQuestionA[i].section] = [];
          newQuestions[newQuestionA[i].section].push(newQuestionA[i]);
        }
        setQuestions(newQuestions);
      }
    } catch (e) {}
  };

  const setAnswer = useCallback(
    (id, answer) => {
      const newAnswers = { ...answers };
      newAnswers[id] = { ...newAnswers[id], answer };
      let isReady = true;
      // check if they've answered everything
      for (const k in newAnswers) {
        if (
          newAnswers[k] === undefined ||
          newAnswers[k].answer === "" ||
          newAnswers[k].answer === undefined
        ) {
          isReady = false;
          break;
        }
      }
      if (isReady) setReady(true);
      setAnswers(newAnswers);
      closeModal();
    },
    [answers]
  );


  const doSkip = useCallback(() => {
    posthog?.capture("skip_sentiment_questionnaire");
    trackSentimentQuestionnaireSkipped();
    history.push("/");
  }, [history, posthog]);

  const sendAnswers = useCallback(async () => {
    try {
      setLoading(true);
      const newAnswers = Object.keys(answers).map((k) => {
        return { answer: answers[k].answer, question_id: k };
      });
      await API.post("/sentiment-answers", newAnswers);
      
      // Track questionnaire submission with completion time
      const completionTime = Math.round((Date.now() - startTimeRef.current) / 1000);
      trackQuestionnaireSubmitted("sentiment", completionTime);
      
      mainStore.handleSentimentQuestionnaire();
      // Optional-chain: arriving without router state (direct nav, onboarding
      // gate) used to THROW here — swallowed by the catch, so the page never
      // navigated at all. Any `from` means the wellness gate sent us.
      if (props.history.location.state?.from) {
        props.history.push("/dashboard/wellness-coach");
      } else {
        props.history.push("/");
      }
    } catch (e) {}
    setLoading(false);
  }, [answers]);

  useEffect(() => {
    getQuestions().then();
  }, []);

  if (mainStore.sentimentQuestionnaire) {
    return (
      <Redirect
        to={{
          pathname: "/",
        }}
      />
    );
  }

  const getSectionIcon = (sectionName) => {
    const lowerSection = sectionName?.toLowerCase() || "";
    if (lowerSection.includes("career") || lowerSection.includes("academic")) return Briefcase;
    if (lowerSection.includes("stress") || lowerSection.includes("coping")) return Brain;
    if (lowerSection.includes("emotional")) return Heart;
    if (lowerSection.includes("physical") || lowerSection.includes("fitness")) return Activity;
    return Target;
  };

  const getSectionColor = (sectionName) => {
    const lowerSection = sectionName?.toLowerCase() || "";
    if (lowerSection.includes("career") || lowerSection.includes("academic")) return "from-blue-800 to-blue-900";
    if (lowerSection.includes("stress") || lowerSection.includes("coping")) return "from-purple-800 to-purple-900";
    if (lowerSection.includes("emotional")) return "from-pink-800 to-pink-900";
    if (lowerSection.includes("physical") || lowerSection.includes("fitness")) return "from-green-800 to-green-900";
    return "from-gray-700 to-gray-800";
  };

  return (
    <div className="wrapper__inner">
      <div className="main">
        <LayoutAuth showContainer={false} showIllustration={false}>
          <div className="relative max-w-6xl mx-auto px-6 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-8 mb-8"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 pr-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-600/20 backdrop-blur rounded-xl flex items-center justify-center">
                    <Sparkles size={24} className="text-blue-400" />
                  </div>
                  <h1 className="text-4xl font-bold text-white">
                    Personal Questionnaire
                  </h1>
                </div>
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  This short questionnaire helps us design your Customized Life Coach for personal growth in these core areas:
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 text-gray-300">
                    <Briefcase size={18} className="text-blue-400" />
                    <span className="text-sm font-medium">Academic/Career Functioning</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Brain size={18} className="text-purple-400" />
                    <span className="text-sm font-medium">Stress and Coping</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Heart size={18} className="text-pink-400" />
                    <span className="text-sm font-medium">Emotional Health</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-300">
                    <Activity size={18} className="text-green-400" />
                    <span className="text-sm font-medium">Physical Health/Fitness</span>
                  </div>
                </div>
                <p className="text-gray-400 text-sm mt-4">
                  Once completed, you can start living your best life - it's FREEde!
                </p>
              </div>
              <div className="hidden lg:block">
                <Guy className="w-64 h-auto" />
              </div>
            </div>
          </motion.div>

          {/* Instructions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-yellow-900/20 border border-yellow-700/50 rounded-xl p-4 mb-6"
          >
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-yellow-200 font-medium text-sm">How to answer:</p>
                <p className="text-yellow-300/80 text-sm mt-1">
                  Drag the slider for each question to indicate your level of agreement. 
                  Move left for "Disagree" and right for "Agree". All questions must be answered to continue.
                </p>
              </div>
            </div>
          </motion.div>

          <div className="space-y-6">
            {Object.keys(questions).map((key, sectionIndex) => {
              const SectionIcon = getSectionIcon(key);
              const sectionColor = getSectionColor(key);
              
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: sectionIndex * 0.1 }}
                  className="bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 overflow-hidden"
                >
                  <div className={`bg-gradient-to-r ${sectionColor} p-4 opacity-80`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-white/10 backdrop-blur rounded-lg flex items-center justify-center">
                        <SectionIcon size={20} className="text-gray-300" />
                      </div>
                      <h2 className="text-xl font-semibold text-gray-200">{key}</h2>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-6">
                    {questions[key].map((q, index) => (
                      <motion.div
                        key={q.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={`bg-gray-700/30 rounded-xl p-6 border transition-colors ${
                          answers[q.id]?.answer !== undefined 
                            ? "border-gray-600/50 hover:border-gray-500/50" 
                            : "border-yellow-700/30 hover:border-yellow-600/40"
                        }`}
                      >
                        <div className="flex items-start gap-3 mb-4">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-sm ${
                            answers[q.id]?.answer !== undefined
                              ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                              : "bg-yellow-900/30 text-yellow-500 border border-yellow-700/50"
                          }`}>
                            {q.order}
                          </div>
                          <p className="text-gray-200 flex-1 text-lg">
                            {q.question}
                          </p>
                        </div>
                        <div className="mt-4 px-4">
                          <CustomRangeSlider
                            value={
                              answers[q.id] ? answers[q.id].answer : undefined
                            }
                            onChange={(v) => setAnswer(q.id, v)}
                          />
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-8 bg-gray-800/50 backdrop-blur rounded-2xl border border-gray-700 p-8"
          >
            <div className="flex flex-col items-center">
              {!ready && (
                <div className="mb-6 flex items-center gap-3 text-yellow-400">
                  <AlertCircle size={20} />
                  <p className="text-sm font-medium">Please answer all questions to continue</p>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                <button
                  type="button"
                  onClick={() => sendAnswers()}
                  disabled={loading || !ready}
                  className={`flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105 ${
                    ready
                      ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl"
                      : "bg-gray-700 text-gray-400 cursor-not-allowed"
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} />
                      <span>Complete</span>
                    </>
                  )}
                </button>
                
                {["skip", "skip-login"].includes(
                  mainStore.appConfig.onboarding_sentiment
                ) && (
                  <button
                    type="button"
                    onClick={doSkip}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-xl font-semibold transition-all transform hover:scale-105"
                  >
                    <ChevronRight size={20} />
                    <span>Maybe Later</span>
                  </button>
                )}
              </div>
              
              <div className="mt-6 flex items-center gap-2 text-gray-400">
                <Award size={16} />
                <p className="text-sm">
                  Your responses help us personalize your wellness journey
                </p>
              </div>
            </div>
          </motion.div>
          </div>
        </LayoutAuth>
      </div>
    </div>
  );
};

export default SentimentQuestionnaire;